import {defineMessages, MessageDescriptor} from 'react-intl'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src/types'

import {ContentKey, PolicySubSection} from './types'

/**
 * Map of policy subsections and the keys of the content that will be rendered for each (on mobile in an overlay)
 * This is also used to manage visibility based on what data is available
 */
export const policySubSections: Record<
  PolicySubSection,
  {keys: ContentKey[]; title: MessageDescriptor; description: MessageDescriptor}
> = {
  [PolicySubSection.CheckInInstructions]: {
    ...defineMessages({
      title: {
        id: 'checkInInstructions',
        defaultMessage: 'Check-in instructions'
      },
      description: {
        id: 'checkInInstructionsDescription',
        defaultMessage: 'Everything you need to know for a smooth check in.'
      }
    }),
    keys: ['checkInSpInst', 'checkInMinAge']
  },
  [PolicySubSection.SecurityDeposit]: {
    ...defineMessages({
      title: {
        id: 'securityDeposit',
        defaultMessage: 'Security deposit'
      },
      description: {
        id: 'depositDescription',
        defaultMessage:
          'You’ll be required to pay the fully refundable deposit for this property.'
      }
    }),
    keys: ['deposit']
  },
  [PolicySubSection.GoodToKnow]: {
    ...defineMessages({
      title: {
        id: 'goodToKnow',
        defaultMessage: 'Good to know'
      },
      description: {
        id: 'goodToKnowDescription',
        defaultMessage: 'Additional policies and info about this property.'
      }
    }),
    keys: ['checkInInst', 'policies', 'license']
  },
  [PolicySubSection.OptionalFees]: {
    ...defineMessages({
      title: {
        id: 'optionalExtras',
        defaultMessage: 'Optional extras'
      },
      description: {
        id: 'optionalExtrasDescription',
        defaultMessage: 'Additional paid services you can request.'
      }
    }),
    keys: ['feesOpt']
  }
}

/**
 * Get the react-intl message for the title of a section
 */
export const getTitle = (section: PolicySubSection) =>
  policySubSections[section].title

/**
 * Get the react-intl message for the description of a section
 */
export const getDescription = (
  section: PolicySubSection,
  showDepositAmount?: boolean
) => {
  if (showDepositAmount)
    return {
      id: 'depositDescriptionWithAmount',
      defaultMessage: 'per accommodation on arrival.'
    }

  return policySubSections[section].description
}

/**
 * Get available content keys for this subsection and hotel
 */
export const getAvailableContentKeys = (
  hotel: undefined | ContentHotel,
  section: PolicySubSection
) => policySubSections[section].keys.filter(key => Boolean(hotel?.[key]))

/**
 * Check whether any content keys are available for this subsection
 */
export const isSubSectionAvailable = (
  hotel: undefined | ContentHotel,
  section: PolicySubSection
) => getAvailableContentKeys(hotel, section).length > 0

/**
 * Check whether any sections will be rendered at all
 */
export const isAnySubSectionAvailable = (hotel: undefined | ContentHotel) =>
  Boolean(
    Object.keys(policySubSections).find((section: PolicySubSection) =>
      isSubSectionAvailable(hotel, section)
    )
  )

/**
 * Check whether check-in/check-out will be rendered (hotel.checkInEnd is optional)
 */
export const isCheckInCheckOutAvailable = (hotel: undefined | ContentHotel) =>
  Boolean(hotel?.checkInTime && hotel?.checkOutTime)
